/**
 * Calculator for student leaderboard scores based on awards, behavior, and tournament results.
 *
 * Score Formula:
 * - Awards: 10 points per award
 * - Behavior: 2 points per star (0-5 stars)
 * - Tournament: 3 points per win, 2 points per draw, 1 point per loss
 */
export class ClassRollLeaderboardCalculator {
    /**
     * Generate lecture score items array with multipliers defined once and points automatically calculated
     */
    static generateLectureScoreItems(student) {
        const addScoreItem = (label, multiplier, count, unit, unitPlural) => {
            return {
                label,
                unit: count === 1 ? unit : unitPlural || unit + "s",
                count,
                multiplier,
                points: count * multiplier,
            };
        };
        return [
            addScoreItem("Awards", 10, student.awards?.length || 0, "award"),
            addScoreItem("Behavior", 2, student.behavior || 0, "star"),
            addScoreItem("Tournament", 3, student.tournamentWins || 0, "win"),
            addScoreItem("Tournament", 2, student.tournamentDraws || 0, "draw"),
            addScoreItem("Tournament", 1, student.tournamentLosses || 0, "loss", "losses"),
        ];
    }
    /**
     * Generate a detailed breakdown of how the score was calculated from raw data
     * Handles all default values internally
     */
    static calculateLectureScoreBreakdown(student, lectureId, date) {
        const scoreItems = this.generateLectureScoreItems(student);
        const total = scoreItems.reduce((acc, item) => acc + item.points, 0);
        return {
            lectureId,
            date,
            scoreItems,
            total,
        };
    }
    /**
     * Calculate cumulative scores for all students across multiple lectures
     */
    static calculateStudentScoreBreakdowns(lectureStudents) {
        // Group students by studentId
        const studentsMap = new Map();
        // Process each lecture-student entry
        lectureStudents.forEach((lectureStudent) => {
            const { studentId, firstName, lastName, lectureId, date, performanceInput, } = lectureStudent;
            // Initialize student if not exists
            if (!studentsMap.has(studentId)) {
                studentsMap.set(studentId, {
                    studentId,
                    firstName,
                    lastName,
                    lectureBreakdowns: [],
                });
            }
            // Calculate score breakdown for this lecture
            const scoreItems = this.generateLectureScoreItems(performanceInput);
            const total = scoreItems.reduce((acc, item) => acc + item.points, 0);
            const lectureBreakdown = {
                lectureId,
                date,
                scoreItems,
                total,
            };
            studentsMap.get(studentId).lectureBreakdowns.push(lectureBreakdown);
        });
        // Convert to final result format
        const results = [];
        studentsMap.forEach((studentData) => {
            const cumulativeTotal = studentData.lectureBreakdowns.reduce((total, lecture) => total + lecture.total, 0);
            results.push({
                studentId: studentData.studentId,
                firstName: studentData.firstName,
                lastName: studentData.lastName,
                lectureBreakdowns: studentData.lectureBreakdowns,
                cumulativeTotal,
            });
        });
        return results.sort((a, b) => b.cumulativeTotal - a.cumulativeTotal);
    }
}
